"use client";

import React from "react";
import { Search, Target, Rocket, RefreshCw } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const processSteps = [{
  number: "01",
  title: "Workflow Discovery",
  description: "We identify high-value workflows within your operations where AI can drive the most significant measurable impact and ROI.",
  icon: Search
}, {
  number: "02",
  title: "Strategic Roadmap",
  description: "Our team designs a custom AI operating model tailored to your SMB, ensuring technology aligns with your specific business goals.",
  icon: Target
}, {
  number: "03",
  title: "Enablement & Automation",
  description: "We deploy automated solutions and enable your employees to work alongside AI, minimizing friction and maximizing productivity.",
  icon: Rocket
}, {
  number: "04",
  title: "Outcome Optimization",
  description: "Continuous monitoring of performance metrics ensures your AI systems evolve to deliver sustained cost savings and efficiency gains.",
  icon: RefreshCw
}];
export default function OurStrategicRoadmap() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="252791" id="our-strategic-roadmap" className="bg-background py-24 md:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={headerRef} className={cn("max-w-3xl mx-auto text-center mb-16 md:mb-24 transition-all duration-700 ease-out", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <span className="text-chart-1 font-default font-semibold tracking-widest uppercase text-sm mb-4 block">
            Our Methodology
          </span>
          <h2 className="text-3xl md:text-5xl font-default font-bold text-foreground mb-6">
            Our Strategic Roadmap
          </h2>
          <p className="text-foreground/70 text-lg md:text-xl font-default max-w-2xl mx-auto">
            A structured, ROI-focused approach to transforming your business workflows into high-performance AI operations.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative">
          {}
          <div className="hidden lg:block absolute top-12 left-[10%] right-[10%] h-px bg-border z-0" />

          {processSteps.map((step, i) => {
          const {
            ref: cardRef,
            isVisible: isCardVisible
          } = useRevealOnScroll({
            threshold: 0.1
          });
          return <div data-index={i} key={i} ref={cardRef} className={cn("transition-all duration-700 ease-out relative z-10", isCardVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")} style={{
            transitionDelay: `${i * 150}ms`
          }}>
                <Card className="bg-card border-border hover:border-chart-1/50 transition-all duration-300 h-full rounded-lg overflow-hidden group shadow-sm hover:shadow-md">
                  <CardHeader className="pb-4 relative">
                    <div className="flex justify-between items-start mb-6">
                      <div className="p-3 rounded-lg bg-accent group-hover:bg-chart-1/10 transition-colors duration-300">
                        <step.icon className="size-6 text-chart-1 group-hover:text-chart-2 transition-colors duration-300" strokeWidth={1.5} />
                      </div>
                      <span className="text-4xl font-bold text-chart-1 font-mono opacity-20 group-hover:opacity-40 transition-opacity">
                        {step.number}
                      </span>
                    </div>
                    <CardTitle className="text-xl text-foreground font-default font-semibold">
                      {step.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-foreground/70 text-sm leading-relaxed font-default">
                      {step.description}
                    </p>
                  </CardContent>
                  
                  {}
                  <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-chart-1/0 to-transparent group-hover:via-chart-1/50 transition-all duration-500" />
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}